import Moment from 'moment'

//Services
import I18n, { isTranslationKey } from 'APP/I18n'

export interface Member {
  id: string
  app_id?: string
  avatar_url?: string
  first_name?: string
  last_name?: string
  props?: {
    picture?: string
    app_id?: string
  }
}

export interface Practitioner extends Member {}

export interface Post {
  user_id: string
  message?: string
  create_at?: number
  update_at?: number
  actionType?: string
  actionContext?: {
    text?: string
  }
  filenames?: string
  file_ids?: string
  localFile?: string
}

export interface Channel {
  channelId: string
  header?: string
  order?: string[]
  posts?: Record<string, Post>
  allMembers?: string[]
  archived?: boolean
  lastViewedAt?: number
}

export interface Episode {
  id: string
  healthIssueTypeId?: string
  outcomeId?: string
  botStatus?: string
}

export interface Practitioners {
  [key: string]: Practitioner
}

export interface Login {
  messagingUserId?: string
}

export interface HealthIssueTypeIcons {
  [key: string]: string
}

export interface FormattedLastPost {
  user?: Member
  message: string
  date: string
}

export interface FormattedEpisodeItem {
  healthIssueTypeIcon?: string
  title: string
  episodeId: string
  channelId: string
  participantNames: string
  concern?: string
  lastPost: FormattedLastPost
  unread: boolean
  busy: boolean
  lastMessageTimestamp?: number
  outcomeId?: string
}

export interface FormattedRecentEpisodeItem {
  id: string
  title: string
  subtitle: string
  lastPostDate?: number
  formattedLastPostDate?: string
  isBotActive: boolean
  healthIssueTypeIcon?: string
  avatarUrl?: string
  outcomeId?: string
}

export interface History {
  orderedEpisodes?: (string | number)[]
  episodes: Record<string | number, Episode>
  channels: Record<string | number, Channel>
  practitioners: Practitioners
}

export const getFormattedEpisodeItem = (
  episode: Episode,
  channel: Channel,
  practitioners: Practitioners,
  login: Login,
  healthIssueTypeIcons: HealthIssueTypeIcons
): FormattedEpisodeItem => {
  const members = getAllMembersFromPosts(channel, practitioners)
  const concern = channel && channel.header

  const { rawLastPost, lastPost } = getFormattedLastPost(members, channel)
  const participantNames = getLastPostUserName(members, lastPost, login)
  // busy is an intermediate state where the posts are not all fetched yet
  return {
    healthIssueTypeIcon:
      episode?.healthIssueTypeId && healthIssueTypeIcons[episode.healthIssueTypeId],
    title: getEpisodeTitle(episode),
    episodeId: episode.id,
    channelId: channel.channelId,
    participantNames,
    concern,
    lastPost,
    unread:
      channel.lastViewedAt !== undefined && channel.lastViewedAt < (rawLastPost.create_at || 0),
    busy: channel.order ? channel.order.length === 0 : false,
    lastMessageTimestamp: rawLastPost.create_at,
    outcomeId: episode.outcomeId,
  }
}

// TODO: Remove this entire thing. Should come from the store.
export const getAllMembersFromPosts = (
  channel: Channel,
  practitioners: Practitioners
): Member[] => {
  let membersFromPosts: Member[] = []
  if (
    channel.order instanceof Array &&
    channel.posts instanceof Object &&
    practitioners instanceof Object
  ) {
    let addedMembers: string[] = []
    channel.order.forEach((id) => {
      const mmUserId = channel.posts![id].user_id
      if (addedMembers.indexOf(mmUserId) === -1) {
        addedMembers.push(mmUserId)
        const user =
          practitioners[mmUserId] ||
          (channel.allMembers && channel.allMembers.find((u) => u === mmUserId))
        // found practitioner
        if (user?.app_id) {
          const avatarUrl = user.avatar_url || (user.props && user.props.picture)
          membersFromPosts.push({
            ...user,
            ...{ props: { picture: avatarUrl, app_id: user.props && user.props.app_id } },
          })
          // found user
        } else {
          // looks like logic issue but keeping as is for now
          membersFromPosts.push({
            id: user as unknown as string,
          })
        }
      }
    })
  }
  return (membersFromPosts && membersFromPosts.length > 0 && membersFromPosts) || []
}

export const getFormattedLastPost = (
  members: Member[],
  channel: Channel
): { rawLastPost: Post; lastPost: FormattedLastPost } => {
  const rawLastPost: Post =
    (channel.order &&
      channel.order.length > 0 &&
      channel.posts &&
      channel.posts[channel.order[0]]) ||
    ({} as Post)
  let lastPost: FormattedLastPost = {
    user: members.find((a) => a.id === rawLastPost.user_id),
    message: (rawLastPost.message || '').trim(),
    date: rawLastPost.update_at
      ? Moment(parseInt(String(rawLastPost.update_at))).fromNow(true)
      : '',
  }

  if (rawLastPost) {
    if (rawLastPost.actionType === 'videoCall')
      lastPost.message = I18n.t('ConversationScreen.videoConsultation')
    if (rawLastPost.actionType === 'carePlan')
      lastPost.message = I18n.t('ConversationScreen.carePlan')
    if (rawLastPost.actionType === 'createChild')
      lastPost.message = rawLastPost.actionContext?.text || ''
    if (rawLastPost.actionType === 'takeIdPhoto')
      lastPost.message = rawLastPost.actionContext?.text || ''

    const hasAttachment = rawLastPost.filenames || rawLastPost.file_ids || rawLastPost.localFile
    if (hasAttachment) {
      lastPost.message = I18n.t('ConversationScreen.snippets.attachment')
    }
  }
  return { rawLastPost, lastPost }
}

export const getLastPostUserName = (
  members: Member[],
  lastPost: FormattedLastPost,
  login: Login
): string => {
  const lastPostUserId = lastPost?.user?.id
  const user = members.filter((member) => member.id === lastPostUserId)
  const { first_name, last_name } = user?.[0] || {}
  const name = [first_name, last_name].filter((el) => el).join(' ')
  if (name) {
    return name
  } else if (user?.[0]?.id === login?.messagingUserId) {
    return I18n.t('EpisodeComponent.you')
  } else {
    return I18n.t('Common.careTeam')
  }
}

const getFormattedRecentEpisodeItem = (
  episode: Episode,
  channel: Channel,
  practitioners: Practitioners,
  login: Login,
  healthIssueTypeIcons: HealthIssueTypeIcons
): FormattedRecentEpisodeItem => {
  const members = getAllMembersFromPosts(channel, practitioners)

  const { rawLastPost, lastPost } = getFormattedLastPost(members, channel)

  const postUserName = lastPost?.user?.id && getLastPostUserName(members, lastPost, login)

  const userId = (lastPost && lastPost.user && lastPost.user.id) || null
  const user = practitioners && userId && practitioners[userId]
  const avatarUrl = user ? user.avatar_url || (user.props && user.props.picture) : undefined

  return {
    id: episode.id,
    title: episode?.healthIssueTypeId ? getEpisodeTitle(episode) : postUserName || '',
    subtitle: lastPost.message,
    lastPostDate: rawLastPost.create_at,
    formattedLastPostDate: rawLastPost.create_at
      ? Moment.utc(rawLastPost.create_at).local().fromNow()
      : undefined,
    isBotActive: episode.botStatus === 'active',
    healthIssueTypeIcon:
      episode?.healthIssueTypeId && healthIssueTypeIcons[episode.healthIssueTypeId],
    avatarUrl,
    outcomeId: episode.outcomeId,
  }
}

export const isEpisodeVisible = (
  episode: Episode | undefined,
  channel: Channel | undefined
): boolean => Boolean(episode && channel && !channel.archived)

export const getRecentMessages = (
  login: Login,
  history: History,
  amountOfDays: number = 30,
  healthIssueTypeIcons?: HealthIssueTypeIcons
): FormattedRecentEpisodeItem[] | undefined => {
  const { orderedEpisodes, episodes, channels, practitioners } = history

  const thirtyDaysAgo = Moment().subtract(amountOfDays, 'days').startOf('day')
  const availableEpisodes = orderedEpisodes?.filter((id) =>
    isEpisodeVisible(episodes[id], channels[id])
  )

  const formattedEpisodes = availableEpisodes
    ?.map((id) =>
      getFormattedRecentEpisodeItem(
        episodes[id],
        channels[id],
        practitioners,
        login,
        healthIssueTypeIcons || {}
      )
    )
    ?.filter((episode) =>
      episode.lastPostDate ? Moment(episode.lastPostDate).isAfter(thirtyDaysAgo) : false
    )
    .slice(0, 3)

  return formattedEpisodes
}

export const getEpisodeTitle = (episode: Episode): string => {
  const healthIssueTypeKey = `health-issue-types:${episode?.healthIssueTypeId}`
  if (episode?.healthIssueTypeId && isTranslationKey(healthIssueTypeKey)) {
    return I18n.t(healthIssueTypeKey)
  } else {
    // there is no translation for the issue type id
    // or there is no issue type id yet (going through intake)
    return I18n.t('EpisodeComponent.newChat')
  }
}
